import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { FaWater, FaChartLine, FaArrowUp, FaArrowDown, FaMinus } from "react-icons/fa";
import StatusIcon from './StatusIcon';
import SparklineChart from './SparklineChart';
import { useFeatures } from '../../../hooks/useFeatures';

/**
 * Card principal de reservatório — redesign premium Fase 1
 * Exibe header, visualização do nível de água, informações e ações
 * Componentes Header e Actions consolidados inline (YAGNI - sem reutilização)
 */
const ReservatorioCard = ({
    reservatorio,
    statusComm,
    isLoading
}) => {
    const navigate = useNavigate();
    const { hasAccessToFeature } = useFeatures();
    const getStatus = () => {
        if (statusComm?.problema || reservatorio.estado === 'canal_sem_dados' || reservatorio.estado === 'sem_dados') return 'warning';
        if (reservatorio.nivelPorcentagem === 'Indefinido') return 'warning';
        if (reservatorio.nivelPorcentagem <= 20) return 'danger';
        return 'normal';
    };

    const status = getStatus();

    const displayNivelPorcentagem = reservatorio.nivelPorcentagem === 'Indefinido'
        ? 'Indefinido'
        : `${Number(reservatorio.nivelPorcentagem).toFixed(1)}%`;

    const displayNivelLitros = reservatorio.nivelLitros === 'Indefinido'
        ? 'Indefinido'
        : `${Number(reservatorio.nivelLitros).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} L`;

    const displayHeightPercentage = reservatorio.nivelPorcentagem === 'Indefinido'
        ? 10
        : Number(reservatorio.nivelPorcentagem);

    const tendencyText = reservatorio.tendencia === 'subindo' ? 'Subindo' : 
                         reservatorio.tendencia === 'descendo' ? 'Descendo' : 'Estável';

    // Fallback robusto para quando o problema não vem do status global mas está marcado no nó do tanque
    const effectiveStatusComm = statusComm?.problema 
        ? statusComm 
        : (reservatorio.estado === 'canal_sem_dados' || reservatorio.estado === 'sem_dados') 
            ? {
                problema: true,
                status: reservatorio.estado,
                mensagem: 'Canal sem medições',
                minutosOffline: 0
            } 
            : statusComm;

    return (
        <div className={`reservatorio-quadrant ${status}`}>
            {/* Header */}
            <div className="reservatorio-header">
                <FaWater className={`card-icon ${status}`} />
                <h3>{reservatorio.nome}</h3>
                {reservatorio.grupo_interconectado && (
                    <span className="reservatorio-badge-meta">
                        Grupo: {reservatorio.grupo_interconectado}
                    </span>
                )}
                <div className="status-container" style={{ marginLeft: 'auto' }}>
                    <StatusIcon
                        status={effectiveStatusComm}
                        ultimaLeitura={reservatorio.ultimaLeitura}
                        className={isLoading ? 'checking' : ''}
                    />
                </div>
            </div>

            <div className="reservatorio-content">
                {/* Visualização do nível de água */}
                <div className={`reservatorio ${status}`}>
                    {reservatorio.nivel_maximo != null && (
                        <div className="alert-line alert-line--max" style={{ bottom: `${reservatorio.nivel_maximo}%` }}>
                            <span className="alert-line__label">Máx {reservatorio.nivel_maximo}%</span>
                        </div>
                    )}
                    {reservatorio.nivel_minimo != null && (
                        <div className="alert-line alert-line--min" style={{ bottom: `${reservatorio.nivel_minimo}%` }}>
                            <span className="alert-line__label">Mín {reservatorio.nivel_minimo}%</span>
                        </div>
                    )}
                    <div
                        className={`water-level ${status}`}
                        style={{
                            height: `${Math.max(3, displayHeightPercentage)}%`
                        }}
                    >
                        {/* A onda é gerada via CSS (::before/after) */}
                    </div>
                </div>

                {/* Informações de nível */}
                <div className="level-info">
                    <div className="level-main-row">
                        <span className={`level-percentage ${status}`}>
                            {displayNivelPorcentagem}
                        </span>
                        {reservatorio.tendencia && (
                            <span className={`tendencia-icon ${reservatorio.tendencia}`} title={`Nível ${tendencyText}`}>
                                {reservatorio.tendencia === 'subindo' ? <FaArrowUp /> : 
                                 reservatorio.tendencia === 'descendo' ? <FaArrowDown /> : <FaMinus />}
                                <span className="tendencia-text">{tendencyText}</span>
                            </span>
                        )}
                    </div>

                    <div className="level-litros-container">
                        <span className="level-litros">
                            <strong>{displayNivelLitros}</strong> {reservatorio.capacidade_litros ? `de ${Number(reservatorio.capacidade_litros).toLocaleString('pt-BR')} L` : ''}
                        </span>
                    </div>

                    {/* Novo design para sparkline interativo */}
                    {reservatorio.sparkline && reservatorio.sparkline.length > 0 && (
                        <div className="sparkline-interactive-box">
                            <SparklineChart 
                                data={reservatorio.sparkline} 
                                width={160} height={50}
                                color={status === 'danger' ? 'var(--color-danger)' : status === 'warning' ? 'var(--color-warning)' : 'var(--color-primary)'}
                                isDown={reservatorio.tendencia === 'descendo'}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Actions com ícones */}
            <div className="reservatorio-actions">
                {hasAccessToFeature('reservatorios_analytics') && (
                    <button
                        className="btn-outline-soft"
                        style={{ width: '100%' }}
                        onClick={() => navigate(`/reservatorios/analytics/${reservatorio.id}`, { state: { reservatorio } })}
                    >
                        <FaChartLine className="btn-outline-soft__icon" />
                        <span>Gráficos & Análise Profunda</span>
                    </button>
                )}
            </div>
        </div>
    );
};

ReservatorioCard.propTypes = {
    reservatorio: PropTypes.shape({
        id: PropTypes.string.isRequired,
        nome: PropTypes.string.isRequired,
        nivelPorcentagem: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]).isRequired,
        nivelLitros: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]).isRequired,
        status: PropTypes.string,
        modulo_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        channelnum: PropTypes.number,
        numero_reservatorio: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }).isRequired,
    statusComm: PropTypes.shape({
        problema: PropTypes.bool,
        status: PropTypes.string,
        mensagem: PropTypes.string,
        minutosOffline: PropTypes.number,
        ultimaAtualizacao: PropTypes.string
    }),
    isLoading: PropTypes.bool
};

export default ReservatorioCard;
